/**
 * @file     PropertyNotificationBadge.tsx
 * @purpose  Notification count badge over the account icon, site-wide.
 *           Anonymous users: local liked-properties count (entices sign-in).
 *           Authenticated users: unread activity from the shared notification
 *           center store used by the dashboard bell.
 *           @/components/properties/WishlistContext
 */
'use client';

import { useContext, type ReactNode } from 'react';
import Badge from '@mui/material/Badge';
import { useSupabaseUser } from '@/hooks/useSupabaseUser';
import { WishlistContext } from '@/components/properties/WishlistContext';
import { useOptionalNotificationCenter } from '@/core/providers/NotificationCenterProvider';

interface Props {
  children: ReactNode;
}

export const PropertyNotificationBadge = ({ children }: Props) => {
  const { user, loading } = useSupabaseUser();
  const wishlist = useContext(WishlistContext);
  const likedCount = wishlist?.count ?? 0;
  const notificationCenter = useOptionalNotificationCenter();
  const unreadCount = notificationCenter?.scope === 'user' ? notificationCenter.unreadCount : 0;

  if (loading) return <>{children}</>;
  const count = user ? unreadCount : likedCount;
  if (count <= 0) return <>{children}</>;

  return (
    <Badge
      badgeContent={count}
      color="error"
      max={99}
      overlap="circular"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{
        '& .MuiBadge-badge': {
          fontSize: 10,
          height: 16,
          minWidth: 16,
          padding: '0 4px',
          fontWeight: 600,
        },
      }}
    >
      {children}
    </Badge>
  );
};
